import { ToolDto } from '@shared/services/apiService/apiService';
import { ToolTypeEnum } from '@shared/constants/campaign.constants';
import { GenderSettings } from '@shared/services/apiService/missing-types';
import { convertLegacyTemplate } from '@shared/utils/messageTemplate';
import {
  ExpandedAgentIdleState,
  ExpandedAgentIdleStateDraft,
  mapIdleStateToDraft,
} from '@features/Admin/Agent/model/idleState';
import { AgentToolsFormValue } from '@features/Admin/Agent/model/types';
import { createExpandedSettingsState } from './constants';
import { AdvancedSettingsHydrationSnapshot } from './types';

const isCustomerIdleConfigured = (draft: ExpandedAgentIdleStateDraft): boolean =>
  [
    draft.customerReplyTimeoutSec,
    draft.customerSilenceThresholdSec,
    draft.customerMaxAttempts,
  ].some((value) => value.trim() !== '');

export const mapAgentToolsToAdvancedSettingsSnapshot = (
  agentId: string,
  idleState: ExpandedAgentIdleState | null | undefined,
  agentNonMcpTools: ToolDto[],
): AdvancedSettingsHydrationSnapshot => {
  const callbackRequestTool = agentNonMcpTools.find(
    (tool) => tool.type === ToolTypeEnum.RequestCallback,
  );
  const voiceMailDetectionTool = agentNonMcpTools.find(
    (tool) => tool.type === ToolTypeEnum.VoiceMailDetection,
  );
  const endCallTool = agentNonMcpTools.find((tool) => tool.type === ToolTypeEnum.EndCall);
  const sendFollowUpMessageTool = agentNonMcpTools.find(
    (tool) => tool.type === ToolTypeEnum.WhatsappMessageSend,
  );
  const generateWhatsappMessageTool = agentNonMcpTools.find(
    (tool) => tool.type === ToolTypeEnum.GenerateWhatsappMessage,
  );
  const transferTool = agentNonMcpTools.find((tool) => tool.type === ToolTypeEnum.TransferCall);
  const callHandoffSignalTool = agentNonMcpTools.find(
    (tool) => tool.type === ToolTypeEnum.CallHandoffSignal,
  );
  const genderDetectionTool = agentNonMcpTools.find(
    (tool) => tool.type === ToolTypeEnum.GenderDetection,
  );
  const initialMessageTemplate = convertLegacyTemplate(
    sendFollowUpMessageTool?.messageTemplate || '',
  );
  const idleStateDraft = mapIdleStateToDraft(idleState);

  return {
    formValues: {
      agentId: agentId || '',
      callbackRequestTool: callbackRequestTool
        ? { ...callbackRequestTool, enabled: true }
        : { enabled: false, isAsync: true },
      voiceMailDetectionTool: voiceMailDetectionTool
        ? { ...voiceMailDetectionTool, enabled: true }
        : { enabled: false, isAsync: false },
      endCallTool: endCallTool
        ? { ...endCallTool, enabled: true }
        : { enabled: false, isAsync: false },
      sendFollowUpMessageTool: sendFollowUpMessageTool
        ? { ...sendFollowUpMessageTool, enabled: true, messageTemplate: initialMessageTemplate }
        : { enabled: false, messageTemplate: '', isAsync: true },
      generateWhatsappMessageTool: generateWhatsappMessageTool
        ? { ...generateWhatsappMessageTool, enabled: true }
        : { enabled: false, isAsync: true },
      transferTool: transferTool
        ? {
            ...transferTool,
            enabled: true,
            phoneNumbers: transferTool.phoneNumbers || [],
          }
        : { enabled: false, phoneNumbers: [], isAsync: true },
      callHandoffSignalTool: callHandoffSignalTool
        ? { ...callHandoffSignalTool, enabled: true }
        : { enabled: false },
      genderDetectionTool: genderDetectionTool
        ? {
            ...genderDetectionTool,
            enabled: true,
            confidenceThreshold: (genderDetectionTool.genderSettings as GenderSettings)
              ?.confidenceThreshold,
            durationThreshold: (genderDetectionTool.genderSettings as GenderSettings)
              ?.durationThreshold,
            behaviorTemplate: (genderDetectionTool.genderSettings as GenderSettings)
              ?.behaviorTemplate,
          }
        : { enabled: false },
    } as AgentToolsFormValue,
    idleStateDraft,
    expandedSettings: {
      ...createExpandedSettingsState(false),
      idle: idleStateDraft.enabled,
      customerIdle: isCustomerIdleConfigured(idleStateDraft),
      whatsapp: Boolean(sendFollowUpMessageTool),
      transfer: Boolean(transferTool),
      gender: Boolean(genderDetectionTool),
    },
  };
};
